import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import Typography from "@mui/material/Typography"
import { FC, useEffect, useRef, useState } from "react"

let mermaidInstance: typeof import("mermaid") | null = null

async function getMermaid() {
  if (!mermaidInstance) {
    mermaidInstance = await import("mermaid")
    mermaidInstance.default.initialize({
      startOnLoad: false,
      theme: "default",
      sequence: {
        actorMargin: 60,
        messageMargin: 40,
        mirrorActors: false,
        showSequenceNumbers: true,
      },
      themeVariables: {
        primaryColor: "#1565c0",
        primaryTextColor: "#ffffff",
        lineColor: "#333333",
        secondaryColor: "#e3f2fd",
        tertiaryColor: "#f5f5f5",
        noteBkgColor: "#fff9c4",
        noteTextColor: "#333333",
        noteBorderColor: "#f9a825",
        actorBkg: "#1565c0",
        actorTextColor: "#ffffff",
        actorLineColor: "#333333",
        signalColor: "#333333",
        signalTextColor: "#222222",
        labelTextColor: "#333333",
        loopTextColor: "#333333",
        altSectionBkgColor: "#f5f5f5",
        altSectionColor: "#333333",
        sequenceNumberColor: "#ffffff",
      },
    })
  }
  return mermaidInstance.default
}

let renderCounter = 0

export const MermaidDiagram: FC<{ chart: string }> = ({ chart }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const render = async () => {
      setLoading(true)
      setError(null)
      try {
        const mermaid = await getMermaid()
        if (cancelled) return
        const id = `mermaid-${++renderCounter}`
        const { svg } = await mermaid.render(id, chart)
        if (cancelled || !containerRef.current) return
        containerRef.current.innerHTML = svg
      } catch (err: any) {
        if (!cancelled) setError(err.message ?? "Failed to render diagram")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    render()
    return () => {
      cancelled = true
    }
  }, [chart])

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Diagram render error: {error}
      </Typography>
    )
  }

  return (
    <Box sx={{ position: "relative", minHeight: 200 }}>
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          "& svg": { maxWidth: "100%", height: "auto" },
          "& .messageText, & .loopText, & text.actor-label": {
            fill: "#222 !important",
          },
          "& .messageLine0, & .messageLine1": {
            stroke: "#333 !important",
          },
          "& marker path": { fill: "#333 !important" },
          display: loading ? "none" : "block",
        }}
      />
    </Box>
  )
}
